import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { toast } from 'react-toastify';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import subscriptionAPI from '../services/subscriptionAPI';
import { FaArrowLeft, FaRocket } from 'react-icons/fa';
import { quizAPI } from '../services/api';
/**
 * AI Quiz Generator Page
 * Generate custom AI-powered quizzes
 * Follows Single Responsibility Principle - handles quiz generation
 */
const AIQuizGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkFeatureAccess, logFeatureUsage, usageStats } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    course_id: '',
    topic: '',
    num_questions: 10,
    difficulty: 'medium',
  });
  const [courses, setCourses] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedQuiz, setGeneratedQuiz] = useState(null);

  const fetchPurchasedCourses = async () => {
    try {
      const response = await quizAPI.getMyPurchasedCourses(user.email);
      if (response.data.success) {
        setCourses(response.data.data.map(course => ({ ...course, purchased: 1 })));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // TODO: Fetch user's enrolled courses
    // For now, using placeholder data
    fetchPurchasedCourses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateQuiz = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }

    // Check feature access
    const access = await checkFeatureAccess('quiz_generator');
    if (!access.has_access) {
      toast.error(access.message || 'Usage limit reached. Please upgrade your plan.');
      navigate('/subscription/plans');
      return;
    }

    setGenerating(true);

    try {
      const response = await subscriptionAPI.generateAIQuiz({
        user_id: user.id,
        ...formData,
      });

      if (response.data.success) {
        console.log('=== Generated Quiz Data ===');
        console.log('Full Response:', response.data);
        console.log('Quiz Data:', response.data.data);
        console.log('First Question:', response.data.data?.questions?.[0]);
        console.log('First Question Options:', response.data.data?.questions?.[0]?.options);
        console.log('========================');
        
        setGeneratedQuiz(response.data.data);
        toast.success('Quiz generated successfully!');
        
        // Log usage
        await logFeatureUsage('quiz_generator', {
          topic: formData.topic,
          num_questions: formData.num_questions,
        });
      } else {
        toast.error(response.data.message || 'Failed to generate quiz');
      }
    } catch (error) {
      console.error('Quiz generation error:', error);
      toast.error(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const usageInfo = usageStats ? {
    used: usageStats.quiz_used || 0,
    limit: usageStats.quiz_limit || 0,
    percentage: usageStats.quiz_limit > 0 
      ? ((usageStats.quiz_used || 0) / usageStats.quiz_limit) * 100 
      : 0,
  } : null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />

      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        {/* Header - Mobile Optimized */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/ai/features')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-3 sm:mb-4 touch-manipulation py-2"
          >
            <FaArrowLeft />
            Back to AI Features
          </button>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            📝 AI Quiz Generator
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Generate custom quizzes on any topic with AI-powered questions
          </p>
        </div>

        {/* Mobile: Usage Info First */}
        {usageInfo && (
          <div className="lg:hidden mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-base font-bold text-gray-900 mb-3">
                Your Usage
              </h3>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Quizzes this month</span>
                    <span className="font-semibold">{usageInfo.used}/{usageInfo.limit === -1 ? '∞' : usageInfo.limit}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        usageInfo.percentage >= 90 ? 'bg-red-500' :
                        usageInfo.percentage >= 70 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: usageInfo.limit === -1 ? '0%' : `${Math.min(usageInfo.percentage, 100)}%` }}
                    />
                  </div>
                </div>

                {usageInfo.percentage >= 80 && usageInfo.limit !== -1 && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-sm text-orange-600 mb-2">
                      {usageInfo.percentage >= 100
                        ? 'Limit reached! Upgrade for more quizzes.'
                        : 'Running low! Consider upgrading.'}
                    </p>
                    <button
                      onClick={() => navigate('/subscription/plans')}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 active:scale-98 text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 touch-manipulation"
                    >
                      Upgrade Plan
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Tips Card - Mobile */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <h3 className="text-base font-bold text-blue-900 mb-2">
                💡 Tips for Better Quizzes
              </h3>
              <ul className="space-y-1.5 text-sm text-blue-800">
                <li>• Be specific with topics for focused questions</li>
                <li>• Start with easy difficulty to build confidence</li>
                <li>• Try 10-15 questions for quick practice</li>
                <li>• Review explanations to learn from mistakes</li>
              </ul>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Quiz Generation Form - Mobile Optimized */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                Create Your Quiz
              </h2>

              <form onSubmit={handleGenerateQuiz} className="space-y-5 sm:space-y-6">
                {/* Course Selection - Mobile Optimized */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Course
                  </label>
                  <select
                    name="course_id"
                    value={formData.course_id}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation appearance-none bg-white"
                  >
                    <option value="">Choose a course...</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Topic - Mobile Optimized */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic *
                  </label>
                  <input
                    type="text"
                    name="topic"
                    value={formData.topic}
                    onChange={handleChange}
                    placeholder="e.g., Binary Search Trees"
                    className="w-full px-4 py-3.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Leave blank to cover all topics in the course
                  </p>
                </div>

                {/* Number of Questions - Mobile Friendly Slider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Number of Questions: <span className="text-blue-600 font-bold">{formData.num_questions}</span>
                  </label>
                  <input
                    type="range"
                    name="num_questions"
                    value={formData.num_questions}
                    onChange={handleChange}
                    min="5"
                    max="50"
                    step="5"
                    className="w-full h-3 rounded-lg appearance-none cursor-pointer bg-gray-200 accent-blue-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5</span>
                    <span>25</span>
                    <span>50</span>
                  </div>
                </div>

                {/* Difficulty Level - Large Touch Targets for Mobile */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Difficulty Level
                  </label>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    {['easy', 'medium', 'hard'].map(level => (
                      <label key={level} className="flex-1">
                        <input
                          type="radio"
                          name="difficulty"
                          value={level}
                          checked={formData.difficulty === level}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={`border-2 rounded-lg px-3 py-3.5 text-center cursor-pointer transition-all touch-manipulation ${
                            formData.difficulty === level
                              ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold shadow-sm'
                              : 'border-gray-300 hover:border-gray-400 active:bg-gray-50'
                          }`}
                        >
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Submit Button - Large for Mobile */}
                <button
                  type="submit"
                  disabled={generating}
                  className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-98 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-wait touch-manipulation text-base sm:text-lg shadow-md"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating Quiz...
                    </>
                  ) : (
                    <>
                      <FaRocket />
                      Generate Quiz (~30 sec)
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Generated Quiz Preview - Mobile Optimized */}
            {generatedQuiz && (
              <div className="mt-6 bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                  Quiz Generated Successfully! 🎉
                </h2>
                <div className="mb-4">
                  <p className="text-gray-600 mb-2 text-sm sm:text-base">
                    Your quiz has been created with {generatedQuiz.questions?.length || 0} questions.
                  </p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                      📚 {generatedQuiz.topic || formData.topic}
                    </span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                      📊 {generatedQuiz.difficulty || formData.difficulty}
                    </span>
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">
                      ⏱️ {generatedQuiz.questions?.length * 60}s
                    </span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    onClick={() => {
                      // Navigate to AI Quiz page with generated questions
                      navigate('/ai-quiz', {
                        state: {
                          questions: generatedQuiz.questions,
                          config: {
                            examDetails: {
                              title: `AI Quiz: ${generatedQuiz.topic || formData.topic}`,
                              difficulty: generatedQuiz.difficulty || formData.difficulty
                            }
                          }
                        }
                      });
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-98 text-white font-semibold py-3.5 px-6 rounded-lg transition-all duration-200 touch-manipulation flex items-center justify-center gap-2"
                  >
                    <span>🚀</span>
                    Start Quiz
                  </button>
                  <button
                    onClick={() => setGeneratedQuiz(null)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 active:scale-98 text-gray-700 font-semibold py-3.5 px-6 rounded-lg transition-all duration-200 touch-manipulation"
                  >
                    Generate New
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop: Usage Info Sidebar */}
          <div className="hidden lg:block">
            {/* Usage Stats */}
            {usageInfo && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Your Usage
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Quizzes this month</span>
                      <span>{usageInfo.used}/{usageInfo.limit === -1 ? '∞' : usageInfo.limit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          usageInfo.percentage >= 90 ? 'bg-red-500' :
                          usageInfo.percentage >= 70 ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: usageInfo.limit === -1 ? '0%' : `${Math.min(usageInfo.percentage, 100)}%` }}
                      />
                    </div>
                  </div>

                  {usageInfo.percentage >= 80 && usageInfo.limit !== -1 && (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-sm text-orange-600 mb-3">
                        {usageInfo.percentage >= 100
                          ? 'Limit reached! Upgrade for more quizzes.'
                          : 'Running low! Consider upgrading.'}
                      </p>
                      <button
                        onClick={() => navigate('/subscription/plans')}
                        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
                      >
                        Upgrade Plan
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-3">
                💡 Tips for Better Quizzes
              </h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>• Be specific with topics for focused questions</li>
                <li>• Start with easy difficulty to build confidence</li>
                <li>• Try 10-15 questions for quick practice</li>
                <li>• Review explanations to learn from mistakes</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AIQuizGenerator;
